import { Autocomplete, Button, Stack, TextField } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import i18n from 'i18next'

/**
 * @typedef {{ value: string, label: string }} Opcion
 * @typedef {{
 *   fechaDesde: string,
 *   fechaHasta: string,
 *   tipo: string,
 *   centro: string,
 *   texto: string,
 * }} Filtros
 */

/**
 * Componente presentacional con los filtros de la pantalla
 * "Solicitudes pendientes de validación". Recibe el estado y las opciones del
 * hook `useFiltrosPendientes` a través del padre y notifica los cambios al
 * instante (autoaplicación). Ver D18 del expediente 1814806.
 *
 * @param {Object} props
 * @param {Filtros} props.filtros Estado actual de los filtros.
 * @param {Array<Opcion>} [props.tiposDisponibles] Opciones del selector de
 *   Tipo, calculadas por el hook a partir de las solicitudes visibles.
 * @param {Array<Opcion>} [props.centrosDisponibles] Opciones del selector de
 *   Centro, calculadas por el hook a partir de las solicitudes visibles.
 * @param {(clave: keyof Filtros, valor: string) => void} props.onChange
 *   Callback invocado cuando cualquier filtro cambia de valor. La
 *   autoaplicación (AC-1814806-5) consiste en invocar `onChange`
 *   inmediatamente desde cada control, sin botón "Buscar".
 * @param {() => void} props.onLimpiar Callback invocado al pulsar
 *   "Limpiar filtros" (AC-1814806-6).
 */
const FiltrosPendientes = ({
  filtros,
  tiposDisponibles = [],
  centrosDisponibles = [],
  onChange,
  onLimpiar,
}) => {
  const valorDesde = filtros.fechaDesde ? dayjs(filtros.fechaDesde) : null
  const valorHasta = filtros.fechaHasta ? dayjs(filtros.fechaHasta) : null

  const errorRango = Boolean(
    filtros.fechaDesde &&
    filtros.fechaHasta &&
    dayjs(filtros.fechaHasta).isBefore(dayjs(filtros.fechaDesde), 'day'),
  )

  const emitirFecha = (clave, valor) => {
    if (!valor) {
      onChange(clave, '')
      return
    }
    onChange(clave, valor.isValid() ? valor.format('YYYY-MM-DD') : '')
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ p: 2, mb: 2, width: '100%' }}
      >
        <DatePicker
          label={i18n.t('solfdsd', { defaultValue: 'Fecha desde' })}
          value={valorDesde}
          onChange={(valor) => emitirFecha('fechaDesde', valor)}
          sx={{ flex: 1, minWidth: 0 }}
          slotProps={{ textField: { variant: 'standard' } }}
        />
        <DatePicker
          label={i18n.t('solfhst', { defaultValue: 'Fecha hasta' })}
          value={valorHasta}
          onChange={(valor) => emitirFecha('fechaHasta', valor)}
          sx={{ flex: 1, minWidth: 0 }}
          slotProps={{
            textField: {
              variant: 'standard',
              error: errorRango,
              helperText: errorRango
                ? i18n.t('solfhmen', {
                    defaultValue: 'La fecha hasta no puede ser anterior a la fecha desde',
                  })
                : '',
            },
          }}
        />
        <Autocomplete
          options={tiposDisponibles}
          value={tiposDisponibles.find((o) => o.value === filtros.tipo) ?? null}
          onChange={(_, opcion) => onChange('tipo', opcion ? opcion.value : '')}
          getOptionLabel={(opcion) => opcion.label ?? ''}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          sx={{ flex: 1, minWidth: 0 }}
          renderInput={(params) => (
            <TextField {...params} label={i18n.t('solftipo', { defaultValue: 'Tipo' })} />
          )}
        />
        <Autocomplete
          options={centrosDisponibles}
          value={centrosDisponibles.find((o) => o.value === filtros.centro) ?? null}
          onChange={(_, opcion) => onChange('centro', opcion ? opcion.value : '')}
          getOptionLabel={(opcion) => opcion.label ?? ''}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          sx={{ flex: 1, minWidth: 0 }}
          renderInput={(params) => (
            <TextField {...params} label={i18n.t('solfcent', { defaultValue: 'Centro' })} />
          )}
        />
        <TextField
          label={i18n.t('buscar', { defaultValue: 'Buscar' })}
          value={filtros.texto ?? ''}
          onChange={(e) => onChange('texto', e.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
          inputProps={{ 'aria-label': i18n.t('buscar', { defaultValue: 'Buscar' }) }}
        />
        <Button variant="text" onClick={onLimpiar} sx={{ flexShrink: 0 }}>
          {i18n.t('limpiar', { defaultValue: 'Limpiar' })}
        </Button>
      </Stack>
    </LocalizationProvider>
  )
}

export default FiltrosPendientes
